/**
 * Shared chrome for every mutating proposal card (symmetric/asymmetric margin,
 * ring, boolean). Owns the parts that are identical across ops — the outlined
 * Card, the hover/focus preview wiring (a11y: keep the overlay while focus
 * moves between the card's own controls), the header + status chip, the failure
 * line, and the Apply/Reject gate — so they cannot drift between ops. Each card
 * supplies only its title, aria-label, and body.
 */
import {
    alpha,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Stack,
    Typography,
} from '@mui/material';
import { AlertTriangle, Check, X } from 'lucide-react';
import { formatCm3 } from '~/utils/units';
import type { ProposalStatus } from '~/agent';

export type { ProposalStatus };

/** One monospace stack for the cards' summary/code styling. */
export const MONOSPACE_FONT_FAMILY =
    'ui-monospace, SFMono-Regular, Menlo, monospace';

const STATUS_CHIP: Record<
    Exclude<ProposalStatus, 'pending'>,
    { label: string; color: string; icon: React.ReactNode }
> = {
    applying: {
        label: 'Applying...',
        color: 'info.main',
        icon: <CircularProgress size={10} sx={{ color: 'inherit' }} />,
    },
    applied: {
        label: 'Applied',
        color: 'success.main',
        icon: <Check size={10} />,
    },
    rejected: {
        label: 'Rejected',
        color: 'error.main',
        icon: <X size={10} />,
    },
    failed: {
        label: 'Failed',
        color: 'error.main',
        icon: <AlertTriangle size={10} />,
    },
};

export interface ProposalCardShellProps {
    readonly title: string;
    readonly ariaLabel: string;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    /** Hover/focus the card to preview the proposed geometry in the viewport. */
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
    readonly children: React.ReactNode;
}

export default function ProposalCardShell({
    title,
    ariaLabel,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
    children,
}: ProposalCardShellProps) {
    const isPending = status === 'pending';
    const isApplying = status === 'applying';
    const chip = status === 'pending' ? undefined : STATUS_CHIP[status];

    return (
        <Card
            variant="outlined"
            sx={{ mb: 1.5, fontSize: 12 }}
            aria-label={ariaLabel}
            onMouseEnter={onPreviewEnter}
            onMouseLeave={onPreviewLeave}
            onFocus={onPreviewEnter}
            onBlur={(e) => {
                // Keep the overlay while focus moves between the card's own
                // controls; clear only when it leaves the card entirely.
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                    onPreviewLeave();
                }
            }}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={1.5}
                py={1}
                sx={(t) => ({
                    backgroundColor: alpha(t.palette.primary.main, 0.06),
                    borderBottom: `1px solid ${t.palette.divider}`,
                })}
            >
                <Typography variant="caption" fontWeight={600}>
                    {title}
                </Typography>
                {chip && (
                    <Chip
                        size="small"
                        icon={chip.icon as React.ReactElement}
                        label={chip.label}
                        variant="outlined"
                        sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            color: chip.color,
                            borderColor: chip.color,
                            '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
                        }}
                    />
                )}
            </Box>

            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack spacing={0.75}>
                    {children}

                    {status === 'failed' && failureReason && (
                        <Typography variant="caption" color="error.main">
                            {failureReason}
                        </Typography>
                    )}
                </Stack>

                {(isPending || isApplying) && (
                    <Stack direction="row" spacing={1} mt={1.5}>
                        <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={!isPending}
                            onClick={onReject}
                            sx={{ flex: 1, textTransform: 'none' }}
                        >
                            Reject
                        </Button>
                        <Button
                            size="small"
                            color="primary"
                            variant="contained"
                            disabled={!isPending}
                            onClick={onApply}
                            sx={{ flex: 1, textTransform: 'none' }}
                        >
                            Apply
                        </Button>
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
}

export interface TargetLineProps {
    readonly targetName: string;
    readonly targetRoiNumber: number;
    readonly currentVolumeCm3: number;
}

/** Resolved-target header shared by the margin, asymmetric, and ring cards. */
export function TargetLine({
    targetName,
    targetRoiNumber,
    currentVolumeCm3,
}: TargetLineProps) {
    return (
        <Typography variant="body2" fontSize={12}>
            Target: <strong>{targetName}</strong>{' '}
            <Typography
                component="span"
                variant="caption"
                color="text.secondary"
            >
                #{targetRoiNumber} · {formatCm3(currentVolumeCm3)}
            </Typography>
        </Typography>
    );
}

export interface ProposalSummaryBoxProps {
    readonly children: React.ReactNode;
}

/** The monospace summary box shared across the mutating proposal cards. */
export function ProposalSummaryBox({ children }: ProposalSummaryBoxProps) {
    return (
        <Box
            sx={(t) => ({
                fontFamily: MONOSPACE_FONT_FAMILY,
                fontSize: '0.78rem',
                color: t.palette.primary.main,
                backgroundColor: alpha(t.palette.common.black, 0.06),
                px: 1,
                py: 0.75,
                borderRadius: 0.5,
            })}
        >
            {children}
        </Box>
    );
}

export interface PreviewedVolumeLineProps {
    readonly label: string;
    readonly value: string;
}

/** Previewed-volume footer caption shared across the proposal cards. */
export function PreviewedVolumeLine({
    label,
    value,
}: PreviewedVolumeLineProps) {
    return (
        <Typography variant="caption" color="text.secondary">
            {label}: {value}
        </Typography>
    );
}
